import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  ClipboardList,
  Search,
  Package,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Trash2,
  X,
  Plus,
  Minus,
  Save,
  LogOut,
  Send,
  Printer,
  Share2,
} from "lucide-react";
import JumlatyLogo from "../components/JumlatyLogo";
import BackToAdminButton from "../components/BackToAdminButton";
import { smartMatch } from "../utils/arabicSearch";
import { confirmDialog } from "../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============== Counter modal — enter counted qty for a single product ===============
const CounterModal = ({ product, currentQty, onClose, onSave, busy }) => {
  const [qty, setQty] = useState(String(currentQty ?? ""));
  useEffect(() => { setQty(String(currentQty ?? "")); }, [currentQty]);

  const handleSave = () => {
    const n = parseInt(qty, 10);
    if (isNaN(n) || n < 0) { toast.error("أدخل رقماً صحيحاً"); return; }
    onSave(n);
  };

  const bump = (d) => setQty((s) => String(Math.max(0, (parseInt(s, 10) || 0) + d)));

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-3" dir="rtl"
      data-testid="mj-counter-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start pb-3 mb-3 border-b-2 border-[#7ED321]/30">
          <div className="flex items-center gap-2 min-w-0">
            {product.image_path ? (
              <img src={`${API}/files/${product.image_path}`} className="w-16 h-16 rounded-xl object-cover border" alt="" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-[#F3F6F4] flex items-center justify-center">
                <Package className="w-6 h-6 text-[#5C6E64]" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-bold text-[#111814] truncate">{product.name_ar}</p>
              <p className="text-[10px] text-[#5C6E64]">{product.category || ""}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="mj-counter-close"><X className="w-5 h-5" /></button>
        </div>

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">العدد الموجود في المخزن (عدّ فعلي)</label>
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => bump(-1)} className="bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] rounded-full w-10 h-10 flex items-center justify-center" data-testid="mj-counter-minus">
            <Minus className="w-5 h-5" />
          </button>
          <input
            type="number" min="0" value={qty}
            onChange={(e) => setQty(e.target.value)}
            data-testid="mj-counter-input"
            className="flex-1 px-3 py-3 rounded-xl border-2 border-[#7ED321] text-center text-3xl font-black"
            dir="ltr" autoFocus
          />
          <button onClick={() => bump(+1)} className="bg-[#ECFDF5] hover:bg-[#D1FAE5] text-[#15803d] rounded-full w-10 h-10 flex items-center justify-center" data-testid="mj-counter-plus">
            <Plus className="w-5 h-5" />
          </button>
        </div>

        <button onClick={handleSave} disabled={busy} data-testid="mj-counter-save"
          className="w-full bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-3 rounded-full flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          حفظ في الجرد
        </button>
      </div>
    </div>
  );
};

// =============== Single session view (mujahez counting) ===============
const SessionView = ({ session, products, onBack, onSubmitted }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(session.items || []);
  const [status, setStatus] = useState(session.status || "open");
  const [activeProduct, setActiveProduct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busySubmit, setBusySubmit] = useState(false);

  // Quick lookup of counted qty by product_id
  const counted = useMemo(() => {
    const m = {};
    items.forEach((it) => { m[it.product_id] = it; });
    return m;
  }, [items]);

  const filtered = useMemo(() => {
    if (!q.trim()) return products;
    // Search across name, category, brand AND the weight string (so "1000"
    // or "كيلو" or "الف" all map to a product labeled "1 كغ" / "1000 غ").
    return products.filter((p) => smartMatch(
      `${p.name_ar || ""} ${p.name_en || ""} ${p.category || ""} ${p.brand_name || ""} ${p.weight_per_piece || ""} ${p.units_per_carton || ""}`,
      q
    ));
  }, [q, products]);

  const totalCounted = items.length;
  const grandUnits = useMemo(() => items.reduce((s, it) => s + (it.counted_qty || 0), 0), [items]);

  const saveItem = async (qty) => {
    if (!activeProduct) return;
    setBusy(true);
    try {
      await axios.post(`${API}/mujahez/inventory-counts/${session.id}/items`, {
        product_id: activeProduct.id,
        counted_qty: qty,
      });
      // Optimistic update
      setItems((prev) => {
        const idx = prev.findIndex((x) => x.product_id === activeProduct.id);
        const next = [...prev];
        const row = { product_id: activeProduct.id, counted_qty: qty, counted_at: new Date().toISOString() };
        if (idx >= 0) next[idx] = row;
        else next.push(row);
        return next;
      });
      toast.success(`تم تسجيل ${qty} للمادة`);
      setActiveProduct(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  const removeItem = async (productId) => {
    if (!(await confirmDialog("حذف هذه المادة من الجرد؟"))) return;
    try {
      await axios.delete(`${API}/mujahez/inventory-counts/${session.id}/items/${productId}`);
      setItems((prev) => prev.filter((x) => x.product_id !== productId));
      toast.success("تم الحذف");
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    }
  };

  const submit = async () => {
    if (items.length === 0) { toast.error("لا توجد مواد لتسليمها"); return; }
    if (!(await confirmDialog(`تسليم الجرد للإدارة؟ بعد التسليم لن تستطيع تعديل الإدخالات.\n\nعدد المواد المعدودة: ${items.length}`))) return;
    setBusySubmit(true);
    try {
      await axios.post(`${API}/mujahez/inventory-counts/${session.id}/submit`);
      toast.success("تم تسليم الجرد للإدارة");
      setStatus("submitted");
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusySubmit(false); }
  };

  const buildShareText = () => {
    const lines = [];
    lines.push("📦 جرد المخزن — جملتي");
    lines.push(`العنوان: ${session.title || "—"}`);
    lines.push(`التاريخ: ${(session.created_at || "").slice(0, 10)}`);
    lines.push("─".repeat(20));
    items.forEach((it, i) => {
      const p = products.find((x) => x.id === it.product_id);
      lines.push(`${i + 1}. ${p?.name_ar || "—"}\n   العدد: ${it.counted_qty}`);
    });
    lines.push("─".repeat(20));
    lines.push(`إجمالي البنود: ${items.length}`);
    lines.push(`إجمالي القطع: ${grandUnits}`);
    return lines.join("\n");
  };

  const handleWhatsApp = () => {
    if (items.length === 0) { toast.error("لا توجد بنود"); return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareText())}`, "_blank");
  };

  const handlePrint = () => {
    if (items.length === 0) { toast.error("لا توجد بنود"); return; }
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    const rows = items.map((it, i) => {
      const p = products.find((x) => x.id === it.product_id) || {};
      return `<tr><td style="text-align:center">${i + 1}</td><td>${p.name_ar || "—"}</td><td style="text-align:center;font-weight:800" dir="ltr">${it.counted_qty}</td></tr>`;
    }).join("");
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>جرد - ${session.title || ""}</title>
<style>
@page { size: A4; margin: 12mm }
body { font-family: 'Tajawal', system-ui, sans-serif; padding: 16px; direction: rtl }
h1 { color: #1E3A5F; margin: 0 0 4px }
.sub { color: #5C6E64; font-size: 11px; margin-bottom: 12px }
table { width: 100%; border-collapse: collapse }
th, td { border: 1px solid #E4EBE6; padding: 8px; font-size: 13px; text-align: right }
th { background: #1E3A5F; color: white }
</style></head><body>
<h1>📦 جرد المخزن</h1>
<div class="sub">${session.title || ""} · التاريخ: <span dir="ltr">${(session.created_at || "").slice(0, 10)}</span> · عدد المواد: <span dir="ltr">${items.length}</span></div>
<table><thead><tr><th style="width:36px">#</th><th>المادة</th><th style="width:120px">العدد</th></tr></thead><tbody>${rows}</tbody></table>
</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  const isOpen = status === "open";

  return (
    <div className="space-y-3" data-testid="mj-session-view">
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} data-testid="mj-session-back"
          className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1">
          <ArrowLeft className="w-3 h-3 rotate-180" /> رجوع
        </button>
        <span className={`text-[10px] font-bold px-3 py-1 rounded-full ${isOpen ? "bg-[#F5A623]/20 text-[#a06e10]" : "bg-[#ECFDF5] text-[#15803d]"}`}>
          {isOpen ? "قيد العدّ" : "مُسلَّم"}
        </span>
      </div>

      {/* Session header */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList className="w-5 h-5" />
          <h3 className="font-bold text-lg truncate">{session.title || "جرد"}</h3>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>
            <p className="text-[10px] opacity-80">المواد المعدودة</p>
            <p className="text-2xl font-black" dir="ltr">{totalCounted}</p>
          </div>
          <div>
            <p className="text-[10px] opacity-80">إجمالي القطع</p>
            <p className="text-2xl font-black" dir="ltr">{grandUnits}</p>
          </div>
        </div>
      </div>

      {/* Submit / share buttons */}
      <div className="flex flex-wrap gap-2">
        {isOpen && (
          <button onClick={submit} disabled={busySubmit || items.length === 0} data-testid="mj-session-submit"
            className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2 px-4 rounded-full flex items-center gap-1">
            {busySubmit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            تسليم الجرد للإدارة
          </button>
        )}
        <button onClick={handlePrint} disabled={items.length === 0} data-testid="mj-session-print"
          className="bg-[#1E3A5F] hover:bg-[#152a45] disabled:opacity-50 text-white font-bold py-2 px-4 rounded-full flex items-center gap-1">
          <Printer className="w-4 h-4" /> طباعة
        </button>
        <button onClick={handleWhatsApp} disabled={items.length === 0} data-testid="mj-session-wa"
          className="bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-50 text-white font-bold py-2 px-4 rounded-full flex items-center gap-1">
          <Share2 className="w-4 h-4" /> واتساب
        </button>
      </div>

      {/* Search products */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="mj-product-search"
          placeholder="ابحث عن مادة..."
          className="w-full pr-9 pl-10 py-2.5 rounded-full border-2 border-[#7ED321] bg-white text-sm"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="mj-product-search-clear"
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#FEE2E2] text-[#E85D45] rounded-full w-6 h-6 flex items-center justify-center">
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        )}
      </div>

      {/* Products grid — image + name only, NO stock/prices */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="mj-products-grid">
        {filtered.length === 0 ? (
          <p className="col-span-full text-center py-8 text-[#5C6E64]">لا توجد نتائج</p>
        ) : filtered.map((p) => {
          const c = counted[p.id];
          return (
            <button
              key={p.id}
              onClick={() => isOpen && setActiveProduct(p)}
              disabled={!isOpen}
              data-testid={`mj-product-${p.id}`}
              className={`relative text-right bg-white rounded-2xl p-2 border-2 transition-all ${
                c ? "border-[#7ED321] ring-2 ring-[#7ED321]/20" : "border-[#E4EBE6] hover:border-[#7ED321]"
              } ${!isOpen ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              {c && (
                <span className="absolute top-1 left-1 bg-[#7ED321] text-white rounded-full px-2 py-0.5 text-[10px] font-black z-10" dir="ltr">
                  ✓ {c.counted_qty}
                </span>
              )}
              {p.image_path ? (
                <img src={`${API}/files/${p.image_path}`} alt={p.name_ar} className="w-full aspect-square object-cover rounded-xl" />
              ) : (
                <div className="w-full aspect-square bg-[#F3F6F4] rounded-xl flex items-center justify-center">
                  <Package className="w-8 h-8 text-[#5C6E64]" />
                </div>
              )}
              <p className="text-xs font-bold text-[#111814] truncate mt-1.5">{p.name_ar}</p>
            </button>
          );
        })}
      </div>

      {/* Counted items list (compact) */}
      {items.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E4EBE6] p-3" data-testid="mj-counted-list">
          <p className="text-xs font-bold text-[#1E3A5F] mb-2 border-b pb-1">المواد المُسجَّلة ({items.length})</p>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {items.map((it) => {
              const p = products.find((x) => x.id === it.product_id);
              if (!p) return null;
              return (
                <div key={it.product_id} className="flex items-center gap-2 bg-[#F8FAFC] rounded-lg p-1.5">
                  {p.image_path ? (
                    <img src={`${API}/files/${p.image_path}`} className="w-9 h-9 rounded-lg object-cover" alt="" />
                  ) : <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center"><Package className="w-3.5 h-3.5 text-[#5C6E64]" /></div>}
                  <p className="flex-1 text-xs font-bold truncate">{p.name_ar}</p>
                  <span className="bg-[#7ED321] text-white rounded-full px-2.5 py-0.5 text-xs font-black" dir="ltr">{it.counted_qty}</span>
                  {isOpen && (
                    <button onClick={() => removeItem(it.product_id)} data-testid={`mj-counted-del-${it.product_id}`}
                      className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeProduct && (
        <CounterModal
          product={activeProduct}
          currentQty={counted[activeProduct.id]?.counted_qty}
          onClose={() => setActiveProduct(null)}
          onSave={saveItem}
          busy={busy}
        />
      )}
    </div>
  );
};

// =============== Main page ===============
const MujahezInventory = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const [sessions, setSessions] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        axios.get(`${API}/mujahez/inventory-counts`),
        axios.get(`${API}/mujahez/inventory-products`),
      ]);
      setSessions(sRes.data || []);
      setProducts(pRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحميل");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "mujahez" && user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchAll();
    // eslint-disable-next-line
  }, [user]);

  // Auto-open if deep-linked
  useEffect(() => {
    if (params.id && sessions.length) {
      const s = sessions.find((x) => x.id === params.id);
      if (s) setActiveSession(s);
    }
  }, [params.id, sessions]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C6E64]">جاري التحميل...</div>;
  }

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[#E4EBE6]">
        <div className="max-w-md mx-auto md:max-w-3xl px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex flex-col items-start">
            <JumlatyLogo size="sm" showTagline={false} />
            <span className="text-xs text-[#7ED321] font-bold mt-1">جرد المخزن</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/mujahez-jumlaty")} data-testid="mj-inventory-to-orders"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> الطلبات
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="mj-inventory-logout"
              className="p-2 hover:bg-[#F3F6F4] rounded-full">
              <LogOut className="w-5 h-5 text-[#2D4A3E]" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-3xl px-4 py-6 space-y-4">
        {activeSession ? (
          <SessionView
            session={activeSession}
            products={products}
            onBack={() => { setActiveSession(null); fetchAll(); }}
            onSubmitted={fetchAll}
          />
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <ClipboardList className="w-5 h-5" />
                <h3 className="font-bold text-lg">جلسات الجرد المُسندة إليك</h3>
              </div>
              <p className="text-xs opacity-90">
                اختر جلسة لبدء العدّ. سترى صور المواد وأسماءها فقط — ستُدخل العدد الفعلي الموجود في المخزن لكل مادة.
              </p>
            </motion.div>

            {sessions.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-[#BAE6FD] rounded-2xl p-8 text-center">
                <ClipboardList className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
                <p className="text-[#5C6E64]">لا توجد جلسات جرد حالياً</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="mj-sessions-list">
                {sessions.map((s) => {
                  const isOpen = s.status === "open";
                  return (
                    <button
                      key={s.id}
                      onClick={() => setActiveSession(s)}
                      data-testid={`mj-session-card-${s.id}`}
                      className="w-full text-right bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] hover:border-[#7ED321] transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-[#111814] truncate">{s.title || "جرد"}</p>
                          <p className="text-[11px] text-[#5C6E64]" dir="ltr">
                            {(s.created_at || "").slice(0, 16).replace("T", " ")}
                          </p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          isOpen ? "bg-[#F5A623]/20 text-[#a06e10]" : "bg-[#ECFDF5] text-[#15803d]"
                        }`}>
                          {isOpen ? "قيد العدّ" : <><CheckCircle2 className="w-3 h-3 inline" /> مُسلَّم</>}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[10px] text-[#5C6E64]">
                        <span>عدد البنود: <span className="font-bold text-[#1E3A5F]" dir="ltr">{(s.items || []).length}</span></span>
                        <ArrowLeft className="w-4 h-4 rotate-180" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
      <BackToAdminButton />
    </div>
  );
};

export default MujahezInventory;
